// @flow
import React, { Fragment } from "react";
import { withGlobalContext } from "../../../common/GlobalContext";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import classNames from "classnames";
import filter from "lodash/filter";
import BackgroundSlider from "react-background-slider";

import HeaderSearch from "../HeaderSearch";

import type { $Agent, $Site, $SiteOffice, $Team } from "flow/types";

type $Props = {
  agent: $Agent,
  classes: Object,
  site: $Site,
  team: $Team,
  office: $SiteOffice,
  backgroundImage: string,
  backgroundImages?: Array<string>,
  isCanadian: Function,
};

const FullWidthBgSearchBar = (props: $Props) => {
  const renderImage = () => {
    const { agent, classes, site, team, office } = props;
    let imagePath;

    if (site.userId && agent) {
      imagePath = `${agent.picture}?crop=faces&fit=crop&w=150&h=150`;
    } else if (site.officeId && office) {
      imagePath = office.logo;
    } else if (site.teamId && team) {
      imagePath = team.logoPath;
    }
    return <img className={classes.agentImage} src={imagePath} alt="" />;
  };

  const renderContactInfo = () => {
    const { agent, site, team, office } = props;

    if (site.userId && agent) {
      return (
        <Fragment>
          {agent.directPhone && (
            <a
              href={`tel:${agent.directPhone}`}
              title={`Direct Phone: ${agent.directPhone}`}
            >
              {agent.directPhoneFormatted}
            </a>
          )}
          {agent.cellPhone && (
            <a
              href={`tel:${agent.cellPhone}`}
              title={`Cell Phone: ${agent.cellPhone}`}
            >
              {agent.cellPhoneFormatted}
            </a>
          )}
        </Fragment>
      );
    } else if (site.teamId && team) {
      return (
        <Fragment>
          {team.directPhone && (
            <a
              href={`tel:${team.directPhone}`}
              title={`Direct Phone: ${team.directPhone}`}
            >
              {team.directPhoneFormatted}
            </a>
          )}
          {team.cellPhone && (
            <a
              href={`tel:${team.cellPhone}`}
              title={`Cell Phone: ${team.cellPhone}`}
            >
              {team.cellPhoneFormatted}
            </a>
          )}
        </Fragment>
      );
    } else if (site.officeId && office) {
      return (
        <Fragment>
          {office.officePhone && (
            <a
              href={`tel:${office.officePhone}`}
              title={`Office Phone: ${office.officePhone}`}
            >
              {office.officePhoneFormatted}
            </a>
          )}
        </Fragment>
      );
    }
  };

  const {
    agent,
    classes,
    site,
    team,
    office,
    backgroundImage,
    backgroundImages,
    isCanadian,
  } = props;
  const { pods } = site.uiConfig;
  const coverPod = filter(pods, { section: "homepageCover" });

  const innerPortion = (
    <div style={{ width: "100%" }}>
      <div className={classes.row}>
        <div>{renderImage()}</div>
        <div>
          <Typography
            className={classes.headingLink}
            type="heading4"
            color="#FFF"
          >
            {site.userId && agent ? (
              <a
                href={`/agent/${agent.firstName}-${agent.lastName}-${agent.id}`}
                title={agent.fullName}
              >
                {agent.fullName}
              </a>
            ) : null}
            {site.officeId && office ? (
              <a href={`/offices/${office.slug}/agents`} title={office.name}>
                {office.name}
              </a>
            ) : null}
            {site.teamId && team ? (
              <a
                href={`/offices/${team.name
                  .toLowerCase()
                  .replace(/ /g, "-")}/agents`}
                title={team.name}
              >
                {team.name}
              </a>
            ) : null}
          </Typography>
          {coverPod &&
            coverPod.length &&
            coverPod[0].value !== "fullWidthBackgroundCutoutAndName" && (
              <Typography
                type="heading4"
                className={classes.headingLink}
                color="#FFF"
              >
                {renderContactInfo()}
                <a href="/contact" title="Contact">
                  {site.userId && agent && <div>{agent.email}</div>}
                  {site.teamId && team && <div>{team.email}</div>}
                  {office && <div>{office.email}</div>}
                </a>
              </Typography>
            )}
        </div>
      </div>
      <div className={classNames(classes.row, classes.searchBar)}>
        <div className={classes.innerSearchBar}>
          {coverPod &&
          coverPod.length &&
          coverPod[0].value !== "fullWidthBackgroundImageAndBlackSearch" &&
          coverPod[0].value !== "fullWidthBackgroundBlackSearch" ? (
            <Typography type="heading5" style={{ textAlign: "center" }}>
              Search For Homes
            </Typography>
          ) : null}
          <HeaderSearch
            showColorButtons={
              coverPod &&
              coverPod.length &&
              coverPod[0].value !== "fullWidthBackgroundCutoutAndName"
                ? false
                : true
            }
            isCanadian={isCanadian()}
          />
        </div>
      </div>
    </div>
  );

  if (backgroundImages && backgroundImages.length > 1) {
    return (
      <div className={classes.root}>
        <BackgroundSlider
          images={backgroundImages}
          duration={5}
          transition={2}
        />
        <div className={classes.backgroundImagesOverlay}>{innerPortion}</div>
      </div>
    );
  }

  return (
    <div
      className={classes.root}
      style={{
        background: `linear-gradient(rgba(20, 20, 20, 0.4), rgba(20, 20, 20, 0.4)), url(${backgroundImage}) center/cover no-repeat`,
      }}
    >
      {innerPortion}
    </div>
  );
};

const styles = (theme) => ({
  root: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "100%",
    minHeight: "70vh",
  },
  backgroundImagesOverlay: {
    display: "flex",
    position: "relative",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "center",
    backgroundColor: "#00000050",
    width: "100%",
    minHeight: "70vh",
  },
  row: {
    flex: "0 0 auto",
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  agentImage: {
    display: "block",
    width: "150px",
    height: "auto",
    borderRadius: "50%",
    marginRight: theme.spacing.unit,
  },
  headingLink: {
    fontSize: "18px",
    lineHeight: "1.2em",
    margin: "0",
    padding: "0",
    textShadow: "2px 2px 7px #000",
    fontStyle: "italic",
    "& a": {
      display: "block",
      color: "inherit",
    },
    [theme.breakpoints(900)]: {
      fontSize: "24px",
    },
  },
  searchBar: {
    marginTop: theme.spacing.unit * 1.5,
    padding: (props) => {
      const { pods } = props.site.uiConfig;
      const coverPod = filter(pods, { section: "homepageCover" });
      if (
        coverPod &&
        coverPod.length &&
        coverPod[0].value !== "fullWidthBackgroundCutoutAndName"
      ) {
        return "20px";
      } else {
        return "20px 20px 40px";
      }
    },
    backgroundColor: (props) => {
      const { pods } = props.site.uiConfig;
      const coverPod = filter(pods, { section: "homepageCover" });
      if (
        coverPod &&
        coverPod.length &&
        coverPod[0].value !== "fullWidthBackgroundCutoutAndName"
      ) {
        return "#20242b";
      } else {
        return "#e6e7eb";
      }
    },
  },
  innerSearchBar: {
    position: "relative",
    width: "100%",
    maxWidth: "500px",
    zIndex: theme.zIndex.dropdown,
  },
});

export default withGlobalContext(withStyles(styles)(FullWidthBgSearchBar));
